import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Download, LogOut } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { SubscribersTable, ContactTable } from "@/components/admin/ActivityTable";
import { IssueDownloadCard } from "@/components/admin/IssueDownload";
import { AnalyticsPanel } from "@/components/admin/AnalyticsPanel";
import { MembersPanel } from "@/components/admin/MembersPanel";
import { StatSkeleton } from "@/components/admin/Skeletons";
import SEO from "@/components/seo/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, sub, testId }) => (
  <div
    data-testid={testId}
    className="bg-white border border-[#E7E7E2] rounded-2xl p-7 md:p-8 flex flex-col min-h-[160px]"
  >
    <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[#111111]/45">
      {label}
    </div>
    <div className="mt-4 font-sora text-[46px] md:text-[56px] font-extrabold leading-none tracking-tight text-[#111111]">
      {value}
    </div>
    {sub && (
      <div className="mt-auto pt-6 font-manrope text-[13px] text-[#111111]/55 truncate">
        {sub}
      </div>
    )}
  </div>
);

const AdminDashboard = () => {
  const { admin, logout, getToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = await axios.get(`${API}/admin/stats`, {
          headers: { Authorization: `Bearer ${getToken()}` },
        });
        setStats(data);
      } catch (err) {
        toast.error(
          formatApiError(err?.response?.data?.detail) || "Could not load stats."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [getToken]);

  const downloadCsv = async (kind) => {
    setDownloading(kind);
    try {
      const res = await axios.get(`${API}/admin/export/${kind}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: "blob",
      });
      // try to parse filename from header
      const cd = res.headers["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(cd);
      const filename = match ? match[1] : `vaymilo-${kind}.csv`;
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${filename} downloaded.`);
    } catch (err) {
      toast.error(
        formatApiError(err?.response?.data?.detail) || "Download failed."
      );
    } finally {
      setDownloading(null);
    }
  };

  return (
    <main
      data-testid="admin-dashboard-page"
      className="min-h-screen bg-[#F7F7F4]"
    >
      <SEO title="Admin" path="/admin" noindex />
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#F7F7F4] border-b border-[#E7E7E2]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="font-sora text-[22px] font-extrabold tracking-tight text-[#111111] leading-none"
            >
              Vaymilo<span className="text-[#2563EB]">.</span>
            </Link>
            <span className="hidden sm:inline-block font-mono text-[10px] tracking-[0.22em] uppercase text-[#111111]/50">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span
              data-testid="admin-email-display"
              className="hidden md:inline font-manrope text-sm text-[#111111]/65"
            >
              {admin?.email}
            </span>
            <button
              data-testid="btn-admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 bg-white text-[#111111] font-manrope font-medium text-sm px-4 py-2 rounded-lg border border-[#E7E7E2] hover:border-[#111111] transition-colors"
            >
              <LogOut size={14} />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="overline">— Dashboard</div>
        <h1 className="mt-4 font-sora text-[40px] md:text-[56px] font-extrabold tracking-tight leading-[1.05] text-[#111111]">
          Studio control.
        </h1>
        <p className="mt-4 font-manrope text-[16px] text-[#111111]/65 max-w-xl">
          Quick view of signups and contact messages collected from the site.
          Export full data as CSV at any time.
        </p>

        {/* Stats grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {loading ? (
            <>
              <StatSkeleton />
              <StatSkeleton />
            </>
          ) : (
            <>
              <StatCard
                testId="stat-subscribers"
                label="Subscribers"
                value={stats?.subscribers_total ?? 0}
                sub={
                  stats?.latest_subscriber
                    ? `Latest: ${stats.latest_subscriber.email}`
                    : "No subscribers yet"
                }
              />
              <StatCard
                testId="stat-contact"
                label="Contact messages"
                value={stats?.contact_total ?? 0}
                sub={
                  stats?.latest_contact
                    ? `Latest: ${stats.latest_contact.name} · ${stats.latest_contact.email}`
                    : "No messages yet"
                }
              />
            </>
          )}
        </div>

        {/* Export section */}
        <section className="mt-16">
          <div className="overline">— Export</div>
          <h2 className="mt-4 font-sora text-[28px] md:text-[36px] font-bold tracking-tight text-[#111111]">
            Download CSV.
          </h2>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-[#E7E7E2] border border-[#E7E7E2] rounded-2xl overflow-hidden">
            <div className="bg-white p-7 md:p-8 flex flex-col">
              <div className="flex items-center justify-between">
                <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#111111]/45">
                  subscribers.csv
                </div>
                <span className="font-mono text-[11px] text-[#2563EB]">+</span>
              </div>
              <h3 className="mt-4 font-sora text-[20px] md:text-[22px] font-semibold text-[#111111]">
                Email subscribers
              </h3>
              <p className="mt-2 font-manrope text-[14px] text-[#111111]/65 leading-relaxed">
                Full subscriber list with email, source, and signup timestamp.
              </p>
              <button
                onClick={() => downloadCsv("subscribers")}
                disabled={downloading === "subscribers"}
                data-testid="btn-export-subscribers"
                className="mt-6 inline-flex items-center gap-2 self-start bg-[#111111] hover:bg-[#2563EB] disabled:opacity-60 text-white font-manrope font-medium text-sm px-5 py-2.5 rounded-lg transition-colors"
              >
                <Download size={14} />
                {downloading === "subscribers" ? "Preparing…" : "Download CSV"}
              </button>
            </div>

            <div className="bg-white p-7 md:p-8 flex flex-col">
              <div className="flex items-center justify-between">
                <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-[#111111]/45">
                  contact.csv
                </div>
                <span className="font-mono text-[11px] text-[#2563EB]">+</span>
              </div>
              <h3 className="mt-4 font-sora text-[20px] md:text-[22px] font-semibold text-[#111111]">
                Contact messages
              </h3>
              <p className="mt-2 font-manrope text-[14px] text-[#111111]/65 leading-relaxed">
                Every message submitted through the contact form, including
                sender details.
              </p>
              <button
                onClick={() => downloadCsv("contact")}
                disabled={downloading === "contact"}
                data-testid="btn-export-contact"
                className="mt-6 inline-flex items-center gap-2 self-start bg-[#111111] hover:bg-[#2563EB] disabled:opacity-60 text-white font-manrope font-medium text-sm px-5 py-2.5 rounded-lg transition-colors"
              >
                <Download size={14} />
                {downloading === "contact" ? "Preparing…" : "Download CSV"}
              </button>
            </div>
          </div>
        </section>

        <AnalyticsPanel />

        <MembersPanel />

        {/* Fulfillment */}
        <section className="mt-16">
          <div className="overline">— Fulfillment</div>
          <div className="mt-6">
            <IssueDownloadCard />
          </div>
        </section>

        {/* Recent Activity tables */}
        <section className="mt-16 space-y-10">
          <div>
            <div className="overline">— Recent activity</div>
            <h2 className="mt-4 font-sora text-[28px] md:text-[36px] font-bold tracking-tight text-[#111111]">
              Browse, search, delete.
            </h2>
            <p className="mt-3 font-manrope text-[15px] text-[#111111]/65 max-w-xl">
              Paginated views of everything that lands in your database.
              Search by email or message, and remove anything you don&rsquo;t
              need.
            </p>
          </div>

          <SubscribersTable />
          <ContactTable />
        </section>

        {/* Quick links to other admin pages */}
        <section className="mt-16">
          <div className="overline">— More tools</div>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E7E7E2] border border-[#E7E7E2] rounded-2xl overflow-hidden">
            <Link to="/admin/coupons" data-testid="admin-link-coupons" className="bg-white p-7 hover:bg-[#F7F7F4] transition-colors">
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#111111]/45">Promotions</div>
              <div className="mt-3 font-sora text-[20px] font-semibold text-[#111111]">Coupons & launch codes →</div>
            </Link>
            <Link to="/admin/gallery" data-testid="admin-link-gallery" className="bg-white p-7 hover:bg-[#F7F7F4] transition-colors">
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#111111]/45">Gallery</div>
              <div className="mt-3 font-sora text-[20px] font-semibold text-[#111111]">Moderation queue →</div>
            </Link>
            <Link to="/admin/newsletter" data-testid="admin-link-newsletter" className="bg-white p-7 hover:bg-[#F7F7F4] transition-colors">
              <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#111111]/45">Newsletter</div>
              <div className="mt-3 font-sora text-[20px] font-semibold text-[#111111]">Write & publish →</div>
            </Link>
          </div>
        </section>

        <div className="mt-16 pt-8 border-t border-[#E7E7E2] flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-1 font-manrope text-sm text-[#111111]/65 hover:text-[#111111] transition-colors"
          >
            View public site
            <ArrowUpRight size={14} />
          </Link>
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#111111]/40">
            v1.1
          </span>
        </div>
      </div>
    </main>
  );
};

export default AdminDashboard;
